import React from 'react';

import { Botao, Chip, Modal, Nota } from '../../../ui';
import { AssociadoEnvioSga, CarroEnvioSga } from '../../../api/associado/associadoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataCurta } from '../../../utils/data';
import { ROTULO_VEICULO_ELETRICO } from '../../../utils/veiculoEletrico';
import {
  NOTA_ENVIO_AUTOMATICO,
  mostraPlacaAntiga,
  rotuloEnvioRegistro,
  rotuloTipoRegistro,
} from '../../../utils/envioSgaRegras';

export interface ModalDetalhesEnvioAssociadoProps {
  /** Associado em exibição — null mantém o modal fechado. */
  associado: AssociadoEnvioSga | null;
  ocupado: boolean;
  /** Chave da ação em curso ("associado:<id>" / "carro:<id>"). */
  enviando: string | null;
  onFechar: () => void;
  onEnviarAssociado: (associado: AssociadoEnvioSga) => void;
  onEnviarVeiculo: (placa: string, carroId: string) => void;
  onVerEnvioAssociado: (associado: AssociadoEnvioSga) => void;
  onVerEnvioVeiculo: (carro: CarroEnvioSga) => void;
}

/**
 * Detalhes de um registro do acompanhamento de envios (at-32; reescrito na at-70,
 * frente D/D8).
 *
 * **É aqui que vivem os veículos do associado e todas as ações deles** — a linha da
 * tabela é um índice enxuto (nome, CPF, situação). Era assim no celular desde
 * 03/09/2026; na at-70 passou a valer também no desktop, porque a tabela misturava
 * duas entidades (o associado tingido de laranja e N sub-linhas de veículo) e
 * marcava o estado de cada uma com uma bolinha sem texto.
 *
 * ## O que mudou na at-70
 *
 *   1. **Virou o `Modal` do kit** — foco preso, Esc, clique fora, título `<h2>`.
 *      Antes era `.filtros-overlay` + `.carteira-detalhes-modal`, com listener de
 *      `Escape` próprio e `aria-modal="true"` **sem prender o foco**.
 *   2. 🔴 **O motivo do botão travado saiu do `title`.** *"Envie o associado antes
 *      de enviar o veículo"* só aparecia ao passar o mouse — e esta tela é usada no
 *      celular (lição da at-54/at-65).
 *   3. 🔴 **Pendência virou ÂMBAR.** O `ChipEnvio` usava `.carteira-chip-alerta`,
 *      que é `#ea5d0e` — **a cor da marca** — para dizer "ainda não foi ao SGA".
 *      Isso não é erro nem é da marca (spec 02 §2.3).
 *   4. **A nota do envio automático apareceu.** Ela existe no `ModalDetalhesAdesao`
 *      e no `ModalDetalhesCarteira`; aqui o TI via chips de pendência sem nada
 *      dizendo que o cron reenvia sozinho a cada 2 horas.
 *   5. **"Motocicleta elétrica" é dita por extenso** (at-46/at-54): a "placa"
 *      `BIK0001` é identificação gerada pelo SAAC, e o campo `bikeEletrica` já vinha
 *      na resposta — a tabela usava, o modal não.
 *
 * ⚠️ **As props não mudaram**: a tela continua dona das ações e do estado
 * `enviando`; este componente só apresenta e chama de volta.
 *
 * ⚠️ **`ocupado` da tela NÃO vai para o `ocupado` do `Modal`** (lição da at-65):
 * aquele significa "há uma gravação DENTRO deste diálogo" e travaria o fechar
 * porque a lista ao fundo recarregou. Quem trava o fechamento aqui é `enviando`.
 */
const ModalDetalhesEnvioAssociado: React.FC<ModalDetalhesEnvioAssociadoProps> = ({
  associado,
  ocupado,
  enviando,
  onFechar,
  onEnviarAssociado,
  onEnviarVeiculo,
  onVerEnvioAssociado,
  onVerEnvioVeiculo,
}) => {
  if (!associado) return null;

  const associadoPendente = !associado.addedToSga;
  const estadoAssociado = rotuloEnvioRegistro(associado.addedToSga, 'longo');

  return (
    <Modal
      aberto
      titulo={associado.nomeCompleto}
      descricao={formatCpfCnpj(String(associado.cpfCnpj || ''))}
      tamanho="lg"
      ocupado={enviando !== null}
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Associado
          </h3>

          <Chip variante={estadoAssociado.variante}>{estadoAssociado.texto}</Chip>

          <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-texto-suave">
            <span>Consultor: {associado.consultor || '—'}</span>
            <span>Cadastrado em: {formatarDataCurta(associado.createdAt)}</span>
            <span>Código no SGA: {associado.codigoAssociado || '—'}</span>
          </div>

          {associadoPendente && <Nota className="tw-mt-3">{NOTA_ENVIO_AUTOMATICO}</Nota>}

          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
            {associadoPendente && (
              <Botao
                variante="primario"
                tamanho="sm"
                disabled={ocupado}
                carregando={enviando === `associado:${associado.id}`}
                onClick={() => onEnviarAssociado(associado)}
              >
                Enviar associado
              </Botao>
            )}
            <Botao
              variante="secundario"
              tamanho="sm"
              disabled={ocupado}
              onClick={() => onVerEnvioAssociado(associado)}
            >
              Ver envio
            </Botao>
          </div>
        </section>

        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            {associado.carros.length === 1 ? 'Veículo' : `Veículos (${associado.carros.length})`}
          </h3>

          {associado.carros.length === 0 ? (
            <p className="tw-m-0 tw-text-sm tw-text-texto-suave">
              Nenhum veículo em circulação para este associado.
            </p>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-3">
              {associado.carros.map((carro) => (
                <CardDoVeiculo
                  key={carro.id}
                  carro={carro}
                  ocupado={ocupado}
                  enviando={enviando}
                  associadoPendente={associadoPendente}
                  onEnviarVeiculo={onEnviarVeiculo}
                  onVerEnvioVeiculo={onVerEnvioVeiculo}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
};

const CardDoVeiculo: React.FC<{
  carro: CarroEnvioSga;
  ocupado: boolean;
  enviando: string | null;
  associadoPendente: boolean;
  onEnviarVeiculo: (placa: string, carroId: string) => void;
  onVerEnvioVeiculo: (carro: CarroEnvioSga) => void;
}> = ({ carro, ocupado, enviando, associadoPendente, onEnviarVeiculo, onVerEnvioVeiculo }) => {
  const carroPendente = !carro.addedToSga;
  const estado = rotuloEnvioRegistro(carro.addedToSga, 'longo');
  const tipo = rotuloTipoRegistro(carro.tipo);

  return (
    <div className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-p-3">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <div className="tw-font-marca tw-text-sm tw-font-bold tw-tracking-wider tw-text-texto-titulo">
            {carro.placa}
          </div>
          <div className="tw-text-xs tw-text-texto-suave">
            {/* at-46/at-54: identificação gerada, não placa de carro. */}
            {carro.bikeEletrica ? `${ROTULO_VEICULO_ELETRICO} · ` : ''}
            {carro.modelo}
          </div>
        </div>

        <Chip variante={tipo.variante}>{tipo.texto}</Chip>
      </div>

      {/* O que era o `title` da pílula de tipo. */}
      {tipo.descricao && (
        <p className="tw-m-0 tw-mt-1.5 tw-text-xs tw-text-texto-suave">{tipo.descricao}</p>
      )}

      {mostraPlacaAntiga(carro) && (
        <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-texto-suave">
          Substituiu a placa {carro.placaAntiga}.
        </p>
      )}

      <div className="tw-mt-2">
        <Chip variante={estado.variante}>{estado.texto}</Chip>
      </div>

      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-texto-suave">
        <span>Código no SGA: {carro.codigoVeiculo || '—'}</span>
        {carro.codigoAssociadoNovo?.trim() && (
          <span>Novo titular (código): {carro.codigoAssociadoNovo}</span>
        )}
      </div>

      <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
        {carroPendente && (
          <Botao
            variante="primario"
            tamanho="sm"
            disabled={ocupado || associadoPendente}
            carregando={enviando === `carro:${carro.id}`}
            onClick={() => onEnviarVeiculo(carro.placa, carro.id)}
          >
            Enviar veículo
          </Botao>
        )}
        <Botao
          variante="secundario"
          tamanho="sm"
          disabled={ocupado}
          onClick={() => onVerEnvioVeiculo(carro)}
        >
          Ver envio
        </Botao>
      </div>

      {/* 🔴 O motivo da trava, em TEXTO: o SGA exige o código do associado para
          cadastrar o veículo, então enviar antes seria um 400 garantido. */}
      {carroPendente && associadoPendente && (
        <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
          Envie o associado ao SGA antes do veículo — o cadastro do veículo exige o código do dono.
        </p>
      )}
    </div>
  );
};

export default ModalDetalhesEnvioAssociado;
